import { useState } from "react";
import { Button } from "@/components/ui/button";
import { CardContent } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown, Clipboard, Edit2Icon, Save } from "lucide-react";
import { FileResultRendererMD } from "@/components/ui/visits/file-result-renderer-md";
import { cn, stripMarkdown } from "@/lib/utils";
import { motion } from "framer-motion";
import MarkdownEditor from "@/components/ui/EditorMarkdown";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { toast } from "@/hooks/use-toast";
import { t } from "i18next";
import type { NoteTemplate } from "@/common/types/notesTemplates";

interface Props {
  file: NoteTemplate;
  onSave: (result: string) => Promise<void>;
  loading: boolean;
}

export const CardContentTemplate = (props: Props): React.ReactNode => {
  const [isTemplateOpen, setIsTemplateOpen] = useState(false);
  const [isEditingResult, setIsEditingResult] = useState(false);
  const [newValue, setNewValue] = useState(props.file.result || "");

  const handleCopyClipboard = async (
    text: string | undefined
  ): Promise<void> => {
    if (text != undefined) {
      try {
        await navigator.clipboard.writeText(stripMarkdown(text));
        toast({
          variant: "default",
          description: t("visits.templateViewer.messages.textCopied"),
          duration: 3000,
        });
      } catch {
        // silent fail
      }
    }
  };

  return (
    <CardContent className="p-6 border-t">
      <Collapsible open={isTemplateOpen} onOpenChange={setIsTemplateOpen}>
        <CollapsibleTrigger className="flex w-full items-center justify-between">
          <div className="flex flex-col items-start ">
            <h3 className="text-lg font-bold">{props.file.template}</h3>
            <p className="text-sm text-grey-400">
              {`${props.file.style} - ${props.file.format} - ${props.file.exam}`}
            </p>
          </div>
          <ChevronDown
            className={cn(
              "h-5 w-5 text-grey-400 transition-transform duration-200",
              isTemplateOpen && "transform rotate-180"
            )}
          />
        </CollapsibleTrigger>
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3, ease: [0.645, 0.045, 0.355, 1] }}
          animate={
            isTemplateOpen
              ? { opacity: 1, height: "auto" }
              : { opacity: 0, height: 0 }
          }
        >
          <CollapsibleContent className="mt-4">
            <div className="space-y-4">
              <div className="flex justify-between">
                <div></div>
                <div className="flex gap-2">
                  {!isEditingResult && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => {
                          setIsEditingResult(true);
                        }}
                      >
                        {t("visits.templateViewer.buttons.edit")} <Edit2Icon />
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleCopyClipboard(props.file.result)}
                      >
                        {t("visits.templateViewer.buttons.copyClipboard")}{" "}
                        <Clipboard />
                      </Button>
                    </>
                  )}
                  {isEditingResult && (
                    <>
                      <Button
                        disabled={props.loading}
                        size="sm"
                        variant={"success"}
                        onClick={() => props.onSave(newValue)}
                      >
                        {props.loading && <LoadingSpinner />}
                        <Save />
                        {t("common.save")}
                      </Button>
                      <Button
                        size="sm"
                        variant={"grey"}
                        onClick={() => {
                          setIsEditingResult(false);
                        }}
                      >
                        {t("common.cancel")}
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <div>
                {isEditingResult ? (
                  <MarkdownEditor
                    initValue={props.file.result || ""}
                    onChange={setNewValue}
                  />
                ) : (
                  <FileResultRendererMD result={props.file.result || ""} />
                )}
              </div>
            </div>
          </CollapsibleContent>
        </motion.div>
      </Collapsible>
    </CardContent>
  );
};
